import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, CreditCard } from "@mui/icons-material";
import { FC } from "react";
import dayjs from "dayjs";

import { ICreditTransaction } from "../../types/creditTransaction";

const SOURCE_TYPE_COLORS: Record<
  string,
  "success" | "error" | "warning" | "info" | "default"
> = {
  PREPAYMENT: "success",
  REFUND: "info",
  PAYMENT: "error",
  ADJUSTMENT: "warning",
  ORDER_CANCEL: "info",
  ORDER_ITEM_CANCEL: "info",
};

interface TransactionsTableProps {
  transactions: ICreditTransaction[];
  isLoading: boolean;
  onEdit: (transaction: ICreditTransaction) => void;
  onDelete: (transaction: ICreditTransaction) => void;
  onCreateNew: () => void;
}

const TransactionsTable: FC<TransactionsTableProps> = ({
  transactions,
  isLoading,
  onEdit,
  onDelete,
  onCreateNew,
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (transactions.length === 0) {
    return (
      <Paper
        sx={{
          textAlign: "center",
          p: 6,
          border: "2px dashed",
          borderColor: "divider",
        }}
      >
        <CreditCard sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Nenhuma transação encontrada
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Clique em "Novo Pré-Pagamento" para registrar crédito para uma
          parceira.
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={onCreateNew}>
          Novo Pré-Pagamento
        </Button>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Data</TableCell>
            <TableCell>Parceira</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell>Descrição</TableCell>
            <TableCell align="right">Valor</TableCell>
            <TableCell align="center">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map((transaction) => (
            <TableRow key={transaction.id} hover>
              <TableCell>
                <Typography variant="body2">
                  {dayjs(transaction.created_at).format("DD/MM/YYYY")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {dayjs(transaction.created_at).format("HH:mm")}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {transaction.urban_partner_name}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={transaction.source_type_display}
                  size="small"
                  color={
                    SOURCE_TYPE_COLORS[transaction.source_type] || "default"
                  }
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    maxWidth: 300,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {transaction.description || "-"}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color={
                    parseFloat(transaction.amount) >= 0
                      ? "success.main"
                      : "error.main"
                  }
                >
                  {parseFloat(transaction.amount) < 0 ? "-" : "+"}
                  R$ {Math.abs(parseFloat(transaction.amount)).toFixed(2)}
                </Typography>
              </TableCell>
              <TableCell align="center">
                {transaction.source_type === "PREPAYMENT" && (
                  <>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onEdit(transaction)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onDelete(transaction)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TransactionsTable;
